import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, exact, number, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { ConfirmationModal } from '@molecules';
import { EditRequestModal, GlobalAccessForm, GlobalAccessRequests } from '@organisms';
import { Main } from '@templates';
import { SUCCESS, LOADING } from '@constants/requestPhase';
import { requestAccess, resetPhases, cancelRequestAccess } from '@store/globalAccess/duck';
import { Typography, Flex } from '@mixins';

import GlobalAccessImage from '@assets/images/GlobalAccess.png';
import { ReactComponent as LockIcon } from '@assets/svgs/Lock.svg';
import { theme } from '@styles';

const GlobalAccess = ({ requests, isLoading }) => {
  const dispatch = useDispatch();

  const { cancelAccessRequestPhase, editAccessRequestPhase, requestAccessPhase } = useSelector(
    store => store.globalAccessStore,
  );

  const [status, setStatus] = useState('');
  const [cancelingRequestId, setCancelingRequestId] = useState(0);
  const [editingRequestId, setEditingRequestId] = useState(0);

  const selectedRequest = useMemo(() => requests.find(({ id }) => id === editingRequestId), [editingRequestId]);

  useEffect(() => {
    if (requestAccessPhase === SUCCESS) {
      setStatus('created');
      dispatch(resetPhases());
    }
    if (cancelAccessRequestPhase === SUCCESS) {
      setStatus('canceled');
      dispatch(resetPhases());
    }
    if (editAccessRequestPhase === SUCCESS) {
      setStatus('edited');
      dispatch(resetPhases());
    }

    setEditingRequestId(0);
  }, [cancelAccessRequestPhase, editAccessRequestPhase, requestAccessPhase]);

  const handleRequestEdit = id => () => {
    setEditingRequestId(id);
  };

  const handleEditClose = () => {
    setEditingRequestId(0);
  };

  const handleConfirmationModalClose = () => {
    setStatus('');
    setCancelingRequestId(0);
  };

  const handleRequestCreate = useCallback(request => {
    dispatch(requestAccess(request));
  }, []);

  const handleRequestCancel = requestId => () => {
    setCancelingRequestId(requestId);
  };

  const handleCancelConfirm = () => {
    setCancelingRequestId(0);
    setEditingRequestId(0);

    dispatch(cancelRequestAccess(cancelingRequestId));
  };

  return (
    <Main isLoading={isLoading} isWhiteLabelingOpen={false}>
      <Flex alignItems="start" pt="10px" pr="80px" justifyContent="space-between" width="100%">
        <Flex width="42%" flexDirection="column">
          <Typography mb={15} variant="rightserif-400" fontSize={[30, 34]} lineHeight={0.8}>
            Visit Another Location
          </Typography>
          <Typography mb={[10, 15, 20, 25]} variant="diatype-400" fontSize={['10px', '13px', '16px']}>
            As a member of Mindspace, we welcome you to be able to enjoy and work from most Mindspace locations! Please
            specify the details of your visit, so that we will prepare for your visit and set you up with needed access.
          </Typography>
          <GlobalAccessForm onSubmit={handleRequestCreate} />
        </Flex>
        <Flex
          width="43%"
          height={['140px', '200px', '280px', '360px']}
          background={`url(${GlobalAccessImage}) no-repeat right top`}
          backgroundSize="cover"
        />
      </Flex>
      {!!requests?.length && (
        <>
          <Flex mb="30px" mt="50px" height="1px" width="100%" backgroundColor="#EFECE6" />
          <Typography mb="30px" alignSelf="start" variant="rightserif-400" fontSize={['20px', '26px', '30px', '34px']}>
            Prior Access Requests
          </Typography>
          <Flex mb="20px" width="100%" paddingBottom="20px" borderBottom={`1px solid ${theme.color.black}`}>
            <Typography
              pl="140px"
              width={['41%', '32%', '27%', '20%']}
              textAlign="center"
              variant="diatype-500"
              fontSize={['10px', '12px', '14px']}>
              Location
            </Typography>
            <Typography
              width={['18%', '27%', '31%', '34%']}
              textAlign="center"
              variant="diatype-500"
              fontSize={['10px', '12px', '14px']}>
              Dates of Visit
            </Typography>
            <Typography
              width={['24%', '25%', '26%', '28%']}
              textAlign="center"
              variant="diatype-500"
              fontSize={['10px', '12px', '14px']}>
              Arrival Time
            </Typography>
          </Flex>
          <GlobalAccessRequests
            onCancelClick={handleRequestCancel}
            onEditRequestClick={handleRequestEdit}
            requests={requests}
          />
        </>
      )}
      {!!editingRequestId && (
        <EditRequestModal
          onRequestCancel={handleRequestCancel}
          isLoading={cancelAccessRequestPhase === LOADING || editAccessRequestPhase === LOADING}
          isOpen={!!editingRequestId}
          onClose={handleEditClose}
          request={selectedRequest}
        />
      )}
      {status && (
        <ConfirmationModal
          icon={LockIcon}
          title={
            status === 'created' ? 'You are in!' : `Access Request ${status === 'canceled' ? 'Canceled' : 'Updated'}!`
          }
          text={
            status === 'created'
              ? 'We will prepare for your visit and will send you details on how to access the location'
              : 'We have notified this local team'
          }
          onPrimaryButtonClick={handleConfirmationModalClose}
          isOpen={status}
          primaryButtonText="Done"
        />
      )}
      {!!cancelingRequestId && (
        <ConfirmationModal
          title="Cancel Visit?"
          onPrimaryButtonClick={handleCancelConfirm}
          onSecondaryButtonClick={handleConfirmationModalClose}
          isOpen={!!cancelingRequestId}
          primaryButtonText="Yes, cancel"
          secondaryButtonText="No, keep it"
        />
      )}
    </Main>
  );
};

GlobalAccess.propTypes = {
  requests: arrayOf(
    exact({
      id: number,
      firstDate: string,
      secondDate: string,
      time: string,
      location: exact({
        id: number,
        image: string,
        title: string,
      }),
    }),
  ),
  isLoading: bool.isRequired,
};

GlobalAccess.defaultProps = {
  requests: [],
};

export default GlobalAccess;
